import { SoftwareInfo } from "@sscs/types";
import { parsePurl } from "@sscs/utils";
import {
  MAX_PACKAGE_NAME_LENGTH,
  MAX_PROJECT_NAME_LENGTH,
  PURL_BLACKLIST_CHARS,
  UNSUPPORTED_URL,
  URL_IMPORT_AUTH_TYPE,
  ZERO_WIDTH_NON_BREAKING_SPACE_CHARACTERS_REGEX,
} from "Constants";
import type { useLazyValidateUrlImportQuery } from "Services";
import {
  FileSoftwareInfo,
  PackageFormData,
  ProjectFormData,
  UrlImportData,
  UrlImportForm,
  VersionSoftwareInfo,
} from "Types";
import * as yup from "./yup";

export const getCleanValue = (value: string) =>
  value.replace(ZERO_WIDTH_NON_BREAKING_SPACE_CHARACTERS_REGEX, "").trim();

export const validatePassword = () =>
  yup
    .string()
    .min(8, "Password needs to have at least 8 characters")
    .max(128, "Password can't be longer than 128 characters");

export const limitChars = (charLength = 150) =>
  yup
    .string()
    .max(charLength, `This field cannot be more than ${charLength} characters`);

export const nullableString = yup
  .mixed()
  .transform((value) => (value ? value : null))
  .nullable(true);

export const MIN_PERCENTAGE = 0;
export const MAX_PERCENTAGE = 100;

export const clampPercentageInput = (inputValue: string): number | null => {
  if (!inputValue) {
    return null;
  }

  const value = +inputValue;
  return isNaN(value)
    ? null
    : Math.min(MAX_PERCENTAGE, Math.max(MIN_PERCENTAGE, value));
};

export const generatePercentageSliderSchema = <
  TForm extends Record<string, number | null>,
>({
  firstPercentageName,
  secondPercentageName,
  firstPercentageMin = MIN_PERCENTAGE,
  firstPercentageMax = MAX_PERCENTAGE,
  secondPercentageMin = MIN_PERCENTAGE,
  secondPercentageMax = MAX_PERCENTAGE,
}: {
  firstPercentageName: keyof TForm;
  secondPercentageName: keyof TForm;
  firstPercentageMin?: number;
  firstPercentageMax?: number;
  secondPercentageMin?: number;
  secondPercentageMax?: number;
}): yup.SchemaOf<TForm> =>
  yup.object({
    // Note: no need to check if [firstPercentageName] + [secondPercentageName] = 100.
    // The other value is always updated after the first value changes
    // either by changing the input value or by using the slider.
    [firstPercentageName]: yup
      .number()
      .required()
      .min(
        firstPercentageMin,
        `Value must be greater than or equal to ${firstPercentageMin}`,
      )
      .max(
        firstPercentageMax,
        `Value must be less than or equal to ${firstPercentageMax}`,
      )
      .typeError("Please enter a valid number"),
    [secondPercentageName]: yup
      .number()
      .required()
      .min(
        secondPercentageMin,
        `Value must be greater than or equal to ${secondPercentageMin}`,
      )
      .max(
        secondPercentageMax,
        `Value must be less than or equal to ${secondPercentageMax}`,
      )
      .typeError("Please enter a valid number"),
  }) as unknown as yup.SchemaOf<TForm>;

export const getFileSoftwareInfoFormSchema =
  (): yup.SchemaOf<FileSoftwareInfo> =>
    yup.object({
      version: yup
        .string()
        .required('"Version" is required!')
        .purlSafe(
          `"Version" may not be equal to . or .., or contain these characters: ${PURL_BLACKLIST_CHARS}`,
        )
        .noInvisibleString('"Version" is required!')
        .typeError('"Version" is required!')
        .max(200, 'Max "Version" length is 200 characters'),
      product: yup
        .string()
        .required('"Product" is required!')
        .noInvisibleString('"Product" is required!')
        .typeError('"Product" is required!')
        .max(200, 'Max "Product" length is 200 characters'),
      publisher: yup
        .string()
        .required('"Publisher" is required!')
        .typeError('"Publisher" is required!')
        .max(200, 'Max "Publisher" length is 200 characters'),
      category: yup
        .string()
        .required('"Category" is required!')
        .typeError('"Category" is required!'),
      license: yup
        .string()
        .required('"License" is required!')
        .typeError('"License" is required!'),
      platform: yup
        .string()
        .required('"Platform" is required!')
        .typeError('"Platform" is required!'),
      architecture: yup
        .string()
        .required('"Architecture" is required!')
        .typeError('"Architecture" is required!'),
      notify_on_completion: yup.boolean().default(false),
    });

const commonVersionSchema = yup
  .string()
  .required('"Version" is required!')
  .purlSafe(
    `"Version" may not be equal to . or .., or contain these characters: ${PURL_BLACKLIST_CHARS}`,
  )
  .noInvisibleString('"Version" is required!')
  .typeError('"Version" is required!')
  .max(200, 'Max "Version" length is 200 characters');

export type SoftwareInfoWithReleased = SoftwareInfo & {
  is_released: boolean;
};

export const getEditVersionSoftwareInfoFormSchema = (
  predecessorVersions: string[],
): yup.SchemaOf<SoftwareInfoWithReleased> =>
  getFileSoftwareInfoFormSchema().shape({
    version: commonVersionSchema.uniqueSignificantly(
      predecessorVersions,
      '"Version" already exists',
    ),
    is_released: yup.boolean().default(false),
    release_date: yup.string().nullable().default(null),
  });

export const getVersionSoftwareInfoFormSchema = (
  predecessorVersions: string[],
): yup.SchemaOf<VersionSoftwareInfo> =>
  getEditVersionSoftwareInfoFormSchema(predecessorVersions).shape({
    version: commonVersionSchema.when("replace", {
      is: false,
      then: yup
        .string()
        .uniqueSignificantly(predecessorVersions, '"Version" already exists'),
      otherwise: yup.string(),
    }),
    predecessor_id: yup.string().nullable().default(null),
    force: yup.boolean().default(true),
    replace: yup.boolean().default(false),
    notify_on_completion: yup.boolean().default(false),
  });

const mailboxRegex = /^("?([^"]+)"?\s*)?<([^@>]+@[^@>]+\.[^@>]+)>$/;
const sanitizeRegex = /javascript:|data:/gi;

export const generateEmailSchema = (): yup.SchemaOf<string> =>
  yup
    .string()
    .transform((value) => {
      // Handle mailbox format (ex: "John Doe" <jdoe@example.com>)
      // Return early if not a string to allow .required() to handle it later
      if (!value || typeof value !== "string") return value;

      const sanitized = value.replaceAll(sanitizeRegex, "");
      const matched = sanitized.match(mailboxRegex);

      if (matched && matched[3]) {
        return matched[3].trim();
      }

      return sanitized;
    })
    .max(250, "Max email length is ${max} characters")
    .email("Invalid email(s) found")
    .required("Email address is required");

// UI renders username and password separate from the token
// It shouldn't happen that both are filled. However, we validate just in case to be defensive
export const urlImportFormSchema = (
  validateImportUrl: ReturnType<typeof useLazyValidateUrlImportQuery>[0],
): yup.SchemaOf<UrlImportData> =>
  yup.object({
    url: yup
      .string()
      .required()
      .max(4096)
      .test({
        name: "url-or-purl",
        message: "Must be a valid URL or PURL",
        test: async function (value) {
          if (!value) return true;

          const validationResult = await validateImportUrl(
            {
              url: value,
            },
            true,
          ).unwrap();

          const mightBePurl = value.startsWith("pkg:");

          if (mightBePurl) {
            try {
              parsePurl(value);

              if (!validationResult.supports_download) {
                return this.createError({
                  message: "Importing from this community is not supported",
                });
              }

              return true;
            } catch (error) {
              return error instanceof Error
                ? this.createError({
                    message: `Invalid PURL: ${error.message}`,
                  })
                : false;
            }
          } else {
            try {
              new URL(value);

              if (!validationResult.supports_download) {
                return this.createError({
                  message: UNSUPPORTED_URL,
                });
              }

              return true;
            } catch {
              return false;
            }
          }
        },
      }),
    authType: yup.mixed().oneOf(Object.values(URL_IMPORT_AUTH_TYPE)).required(),
    username: yup.string().when("authType", {
      is: URL_IMPORT_AUTH_TYPE.basic,
      then: (schema) => schema.required("Username is required"),
      otherwise: (schema) =>
        schema.test(
          "should-not-exist",
          "Username cannot be set when auth type is not basic auth",
          (value) => !value,
        ),
    }),
    password: yup.string().when("authType", {
      is: URL_IMPORT_AUTH_TYPE.basic,
      then: (schema) => schema.required("Password is required"),
      otherwise: (schema) =>
        schema.test(
          "should-not-exist",
          "Password cannot be set when auth type is not basic auth",
          (value) => !value,
        ),
    }),
    token: yup.string().when("authType", {
      is: URL_IMPORT_AUTH_TYPE.token,
      then: (schema) => schema.required("Token is required"),
      otherwise: (schema) =>
        schema.test(
          "should-not-exist",
          "Token cannot be set when auth type is not token",
          (value) => !value,
        ),
    }),
  });

export const getUrlImportFormDefaults = (
  data: UrlImportData,
): UrlImportForm => {
  if (data.username && data.password) {
    return {
      ...data,
      authType: URL_IMPORT_AUTH_TYPE.basic,
    };
  } else if (data.token) {
    return {
      ...data,
      authType: URL_IMPORT_AUTH_TYPE.token,
    };
  } else {
    return {
      ...data,
      authType: URL_IMPORT_AUTH_TYPE.none,
    };
  }
};

export const getProjectFormSchema = (): yup.SchemaOf<ProjectFormData> =>
  yup.object({
    project_name: yup
      .string()
      .transform((value) => value ?? "")
      .required("Project name is required")
      .noInvisibleString("Project name should not be blank")
      .purlSafe()
      .typeError("Please enter valid project name")
      .max(
        MAX_PROJECT_NAME_LENGTH,
        `Project name must be at most ${MAX_PROJECT_NAME_LENGTH} characters`,
      ),
    project_description: yup
      .string()
      .max(500, "Max description length is 500 characters"),
  });

export const getPackageFormSchema = (
  takenNames: string[] = [],
): yup.SchemaOf<PackageFormData> =>
  yup.object({
    package_name: yup
      .string()
      .required("Package name is required")
      .noInvisibleString("Package name should not be blank")
      .purlSafe()
      .typeError("Please enter valid package name")
      .max(
        MAX_PACKAGE_NAME_LENGTH,
        `Package name must be at most ${MAX_PACKAGE_NAME_LENGTH} characters`,
      )
      .uniqueSignificantly(
        takenNames,
        "A package with this name already exists in the project. Import always creates a new package",
      ),
    package_description: yup
      .string()
      .max(500, "Max description length is 500 characters"),
  });
